<template>
    <div v-if="loading" class="flex justify-center p-8">
        <i class="pi pi-spin pi-spinner text-4xl text-gray-400"></i>
    </div>

    <div v-else-if="gradeGroups.length === 0" class="text-center p-8 text-gray-500">
        No grade groups found. Click "Add New Grade" to get started.
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        <div v-for="group in gradeGroups" :key="group.id"
            class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
            <!-- Header -->
            <div class="p-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                <div>
                    <h3 class="font-semibold text-lg text-gray-800">{{ group.name }}</h3>
                    <p v-if="group.description" class="text-xs text-gray-500">{{ group.description }}</p>
                </div>
                <span class="text-xs text-gray-500 bg-white px-2 py-1 rounded border border-gray-200">
                    {{ group.gradeBands.length }} Grades
                </span>
            </div>

            <!-- Grades List -->
            <div class="p-0 flex-grow">
                <div v-for="grade in sortedGrades(group.gradeBands)" :key="grade.id || grade.grade"
                    class="flex items-center justify-between p-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors group">
                    <div class="flex items-center gap-3">
                        <div
                            class="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-lg">
                            {{ grade.grade }}
                        </div>
                        <div>
                            <div class="font-medium text-gray-900">{{ grade.name }}</div>
                            <div class="text-xs text-gray-500">
                                Range: {{ grade.minimum }} - {{ grade.maximum }}
                                <span v-if="isTertiary && grade.point" class="ml-2 px-2 py-0.5 bg-primary/10 text-primary-700 rounded-full font-medium">
                                    {{ grade.point }} pts
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button v-if="canUpdate" icon="pi pi-pencil" text rounded severity="secondary" size="small"
                            @click="emit('edit', grade)" data-testid="btn-grade-edit" />
                        <Button v-if="canDelete" icon="pi pi-trash" text rounded severity="danger" size="small"
                            @click="confirmDelete(grade)" data-testid="btn-grade-delete" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useConfirm } from 'primevue/useconfirm';
import type { Grade, GradeGroup } from '@/types/composition-functions';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { useUserStore } from '@/store/userStore';
import { storeToRefs } from 'pinia';
import { can, AppPermissions } from '@/utils/permissions';

const { isTertiary } = useSchoolFeatures();
const userStore = useUserStore();
const { currentUser } = storeToRefs(userStore);

const props = defineProps<{
    gradeGroups: GradeGroup[]
    loading: boolean
}>();

const emit = defineEmits<{
    (e: 'edit', grade: Grade): void
    (e: 'delete', id: string): void
}>();

const confirm = useConfirm();

const canUpdate = computed(() => can(currentUser.value, AppPermissions.GradeSystem.Actions.Update, AppPermissions.GradeSystem.Resource));
const canDelete = computed(() => can(currentUser.value, AppPermissions.GradeSystem.Actions.Delete, AppPermissions.GradeSystem.Resource));

const sortedGrades = (grades: Grade[]) => [...grades].sort((a, b) => (b.minimum ?? 0) - (a.minimum ?? 0));

const confirmDelete = (grade: Grade) => {
  confirm.require({
    message: `Are you sure you want to delete ${grade.name} (${grade.grade})?`,
    header: 'Confirm Delete',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: () => {
      if (grade.id) {
        emit('delete', grade.id);
      }
    },
  });
};
</script>
